import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GET_MONTHLY_TAM_PRIME, VALIDATE_MONTHLY_PRIME } from '@bloomays-lib/adapter.api-talent';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import MonthlyPrimeHeader from '../molecules/MonthlyPrimeHeader';
import MonthlyPrimeTable from '../molecules/MonthlyPrimeTable';
import { GetMonthlyTamPrimeQueryData, TamPrimeLine } from '../../types/monthlyPrime';
import {
  canValidateMonthlyPrime,
  deriveHeaderAmounts,
  formatPrimeMonthLabel,
  getTamDisplayName,
  hasIneligibleMissionCras,
  hasPrimeLineErrors,
  mapAggregateToValidationInput,
} from '../../utils/monthlyPrime.utils';

export type MonthlyPrimeProps = {
  tamEmail?: string;
  tamDisplayName?: string;
  referenceDate?: Date;
};

const MonthlyPrime = ({
  tamEmail: tamEmailOverride,
  tamDisplayName: tamDisplayNameOverride,
  referenceDate: referenceDateOverride,
}: MonthlyPrimeProps) => {
  const { t } = useTranslation(['primes', 'random']);
  const navigate = useNavigate();
  const auth = useAuthStore((state) => state.auth);
  const authTamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const isTamPreview = Boolean(tamEmailOverride);
  const tamEmail = isTamPreview ? tamEmailOverride?.trim() : authTamEmail;
  const defaultReferenceDate = useMemo(() => new Date(), []);
  const referenceDate = referenceDateOverride ?? defaultReferenceDate;
  const monthLabel = formatPrimeMonthLabel(referenceDate);
  const tamDisplayName = tamDisplayNameOverride?.trim() || getTamDisplayName(auth?.user);

  const { loading, error, data } = useQuery<GetMonthlyTamPrimeQueryData>(GET_MONTHLY_TAM_PRIME, {
    skip: !tamEmail,
    variables: {
      tamEmail,
      referenceDate,
    },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'network-only',
  });

  const [validateMonthlyPrime, { loading: isValidating }] = useMutation(VALIDATE_MONTHLY_PRIME, {
    context: { clientName: 'api.talents' },
    onCompleted: () => {
      notify('success', t('validateSuccess'));
      navigate('/primes/annuel');
    },
    onError: (mutationError) => {
      errorLogger(mutationError, { extraInfos: 'validateMonthlyPrime failed on /primes/mois' });
      notify('error', t('validateError'), mutationError);
    },
  });

  const handleValidate = useCallback(() => {
    const aggregate = data?.getMonthlyTamPrime;
    if (!aggregate || !canValidateMonthlyPrime(aggregate)) {
      return;
    }
    void validateMonthlyPrime({
      variables: {
        tamEmail,
        referenceDate,
        prime: mapAggregateToValidationInput(aggregate),
      },
    });
  }, [data?.getMonthlyTamPrime, referenceDate, tamEmail, validateMonthlyPrime]);

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  if (error) {
    errorLogger(error, { extraInfos: 'getMonthlyTamPrime failed on /primes' });
    return <ErrorMessage variant="body1">{t('loadError')}</ErrorMessage>;
  }

  const aggregate = data?.getMonthlyTamPrime;
  const primes: TamPrimeLine[] = loading ? [] : aggregate?.primes ?? [];
  const amounts = !loading && aggregate ? deriveHeaderAmounts(aggregate) : undefined;
  const canValidate = !loading && aggregate ? canValidateMonthlyPrime(aggregate) : false;
  const validateDisabledTooltip =
    !loading && !isValidating && aggregate !== undefined && !canValidate
      ? hasPrimeLineErrors(aggregate)
        ? t('validateDisabledErrorLineTooltip')
        : hasIneligibleMissionCras(aggregate)
        ? t('validateDisabledCraTooltip')
        : undefined
      : undefined;

  return (
    <Container>
      <MonthlyPrimeHeader
        tamDisplayName={tamDisplayName}
        monthLabel={monthLabel}
        amounts={amounts}
        loading={loading}
        canValidate={canValidate}
        isValidating={isValidating}
        validateDisabledTooltip={validateDisabledTooltip}
        onValidate={handleValidate}
      />
      <MonthlyPrimeTable rows={primes} loading={loading} />
    </Container>
  );
};

export default MonthlyPrime;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
